import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import { DateUtils } from './date-utils';
import { isNullOrEmpty, pattern } from './helpers';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { MedicalServiceDTO } from '@auto-generated/ap-platform/care-providers';


export class FormValidators {
	private static readonly emailRegex = new RegExp(pattern('email'));
  private static readonly TIME_REGEX = new RegExp('^(([0-1]?[0-9])|(2[0-3])):[0-5][0-9]$');
  private static readonly INVALID_INTERVAL_ERROR = { invalidInterval: true };

	public static noWhitespace(control: FormControl) {
		const isWhitespace = control.value && control.value.length > 0 && (control.value || '').trim().length === 0;
		return !isWhitespace ? null : {'whitespace': true};
	}

	public static passwordComposition(control: FormControl) {
		const isValidPassword = control.value && control.value.trim().length > 7 && RegExp(pattern('password')).test(control.value);
		return isValidPassword ? null : {'invalidPassword': true};
	}

	public static noSpaces(control: FormControl) {
		const isValid = (control.value || '').indexOf(' ') === -1;
		return isValid ? null : {'whitespace': true};
	}

	public static matchString(exactString: string): ValidatorFn {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			const isValid = control.value !== undefined && control.value === exactString;
			return isValid ? null : {'matchString': true};
		};
	}

	public static roPhoneValidator(control: FormControl) {
		if (!control.value) return null;

	try {
			const phoneNumberUtil = PhoneNumberUtil.getInstance();
		const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(control.value, 'RO');

		return phoneNumberUtil.isValidNumber(phoneNumber) ? null : { 'invalidRoPhoneNumber': true };
	} catch (exception) {
			return { 'invalidRoPhoneNumber': true };
		}
	}

	public static isTime(control: FormControl) {
		const timeRegex = '^(([0-1]?[0-9])|(2[0-3])):[0-5][0-9]$';
		if (control.value) {
			return control.value.toString().match(timeRegex) ? null : {'format': true};
		}
	}

	public static isTimeRounded(minutes: number): ValidatorFn {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			const isValid = (!isNullOrEmpty(control.value) && (Number(control.value.slice(3, 5)) % minutes === 0));
			if (minutes !== 45 && minutes !== 90) {
				return isValid ? null : {'slotInvalid': true};
			}
			return null;
		};
	}

  public static timeIntervalCrossValidation(): ValidatorFn {
    return (form: FormGroup): { [key: string]: any } | null => {
      const intervals = {
        standard: form.get('standardPeriod.timeInterval') as FormGroup,
        online: form.get('onlinePeriod.timeInterval') as FormGroup,
        internal: form.get('internalPeriod.timeInterval') as FormGroup,
      };

      const actives = {
        online: form.get('onlinePeriod.active')?.value,
        internal: form.get('internalPeriod.active')?.value,
      };

      if (!intervals.standard || !intervals.online || !intervals.internal) return null;

      const errors: { [key: string]: any } = {};

      handleIntervalValidation(
        actives.online,
        intervals.online,
        intervals.standard,
        'intervalNotMatching'
      );

      handleIntervalValidation(
        actives.internal,
        intervals.internal,
        intervals.standard,
        'intervalNotMatching'
      );

      if (actives.online && actives.internal) {
        FormValidators._timeIntervalShouldNotMatch(intervals.internal, intervals.online);
      } else if (intervals.internal.errors) {
        cleanupError(intervals.internal, 'intervalShouldNotMatch');
      }

      Object.keys(intervals).forEach(key => {
        const interval = intervals[key];
        mergeErrors(errors, interval);
        mergeErrors(errors, interval.get('from'));
        mergeErrors(errors, interval.get('to'));
      });

      if (Object.keys(errors).length) {
        form.setErrors({ ...form.errors, ...errors });
        return errors;
      }

      form.setErrors(null);
      return null;
    };

    function handleIntervalValidation(
      isActive: boolean,
      interval: FormGroup,
      referenceInterval: FormGroup,
      errorKey: string
    ): void {
      if (isActive) {
        FormValidators._timeIntervalMatch(interval, referenceInterval);
      } else if (interval.errors) {
        cleanupError(interval, errorKey);
      }
    }

    function cleanupError(group: AbstractControl, errorKey: string): void {
      if (group.errors) {
        const { [errorKey]: _, ...remainingErrors } = group.errors;
        group.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
      }
    }

    function mergeErrors(target: { [key: string]: any }, control: AbstractControl | null): void {
      if (control?.errors) {
        Object.assign(target, control.errors);
      }
    }
  }

  public static conditionalTimeGroupSchedule(activeControlName: string, minutes?: number): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const isActive = group.parent?.get(activeControlName)?.value;

      if (!isActive) {
        return null;
      }

      return FormValidators.timeGroupSchedule(minutes)(group);
    };
  }

  public static conditionalTimeControlValidator(activeControlName: string, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent;
      if (!parent) return null;

      const isActive = parent.parent?.get(activeControlName)?.value;
      return isActive ? validator(control) : null;
    };
  }

  public static timeGroupSchedule(minutes?: number): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const [startControl, endControl] = [group.get('from'), group.get('to')];
      const [startTime, endTime] = [startControl?.value, endControl?.value];

      if ([startControl, endControl].some(ctrl => this.isInvalidTimeFormat(ctrl?.value))) {
        return setError(startControl || endControl);
      }

      if (startTime && endTime) {
        const [startDate, endDate] = [this.getDateFromTime(startTime), this.getDateFromTime(endTime)];

        if (endDate <= startDate || (minutes && !this.isMultipleOfMinutes(startDate, endDate, minutes))) {
          return setError(endControl);
        } else {
          startControl.setErrors(null);
          endControl.setErrors(null);
        }
      }

      return null;
    };

    function setError(control: AbstractControl | null): ValidationErrors {
      control?.setErrors(FormValidators.INVALID_INTERVAL_ERROR);
      return FormValidators.INVALID_INTERVAL_ERROR;
    }
  }


  private static isInvalidTimeFormat(time: string): boolean {
	  return time && !this.TIME_REGEX.test(time);
  }

  private static getDateFromTime(time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
  }

  private static isMultipleOfMinutes(startDate: Date, endDate: Date, minutes: number): boolean {
    const differenceInMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
    return differenceInMinutes % minutes === 0;
  }

  private static _timeIntervalMatch(targetInterval: FormGroup, intervalToMatch: FormGroup): void {
    const startTime = targetInterval.get('from')?.value;
    const endTime = targetInterval.get('to')?.value;

    if (startTime && endTime) {
      const [start, end] = this._getDatesFromIntervalForm(targetInterval);
      const [targetStart, targetEnd] = this._getDatesFromIntervalForm(intervalToMatch);

      if (start && end && targetStart && targetEnd) {
        const hasInvalidRange = start < targetStart || end > targetEnd;

        if (hasInvalidRange) {
          const error = { intervalNotMatching: true };
          targetInterval.setErrors(error);
        } else if (targetInterval.errors) {
          const { intervalNotMatching, ...remainingErrors } = targetInterval.errors;
          targetInterval.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
        }
      }
    }
  }


  private static _timeIntervalShouldNotMatch(targetInterval: FormGroup, intervalToNotMatch: FormGroup): void {
    const errorKey = { intervalShouldNotMatch: true };
    const startTime = targetInterval.get('from')?.value;
    const endTime = targetInterval.get('to')?.value;

    if (startTime && endTime) {
      const [startDate, endDate] = this._getDatesFromIntervalForm(targetInterval);
      const [notMatchStart, notMatchEnd] = this._getDatesFromIntervalForm(intervalToNotMatch);

      const overlaps = !(endDate <= notMatchStart || startDate >= notMatchEnd);
      if (overlaps) {
        targetInterval.setErrors({ ...targetInterval.errors, ...errorKey });
      } else if (targetInterval.errors) {
        const { intervalShouldNotMatch, ...remainingErrors } = targetInterval.errors;
        targetInterval.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
      }
    }
  }


  private static _getDatesFromIntervalForm(group: FormGroup): [Date | null, Date | null] {
      const startTime = group.get('from')?.value;
      const endTime = group.get('to')?.value;
      return [this._parseTime(startTime), this._parseTime(endTime)];
    }

  private static _parseTime(timeString: string): Date | null {
      return timeString ? new Date(`1970-01-01T${timeString}:00`) : null;
  }

	public static periodGroupSchedule(group: FormGroup): { [key: string]: any } {
		const invalidValue = {error: 'Invalid!'};
		const groupMorning = (group.controls['MORNING'] as FormGroup);
		const groupAfternoon = (group.controls['AFTERNOON'] as FormGroup);

		if (groupAfternoon.controls['from'].value && groupMorning.controls['to'].value
			&& groupAfternoon.controls['from'].value <= groupMorning.controls['to'].value
			&& (groupAfternoon.controls['from'].value.length > 0 && groupMorning.controls['to'].value.length > 0)) {
			groupAfternoon.controls['from'].setErrors(invalidValue);
			groupAfternoon.controls['from'].markAsDirty();
			groupAfternoon.controls['from'].markAsTouched();
			return invalidValue;
		}

		groupAfternoon.controls['from'].setErrors(null);
		return null;
	}

	public static matchFields(formGroup: FormGroup) {
		const formControls: FormControl[] = [];

		if (!formGroup.controls) {
			return undefined;
		}

		Object.keys(formGroup.controls).forEach(key => {
			formControls.push(formGroup.controls[key] as FormControl);
		});

		let firstControl: FormControl;

		if (formControls.length < 2) {
			return undefined;
		}

		formControls.forEach((control, index) => {
			if (index === 0) {
				firstControl = control;
			} else {
				if (firstControl.value !== control.value) {
					control.setErrors({match_fields_error: true});
				} else {
					control.setErrors(null);
				}
			}
		});
	}

	/**
	 * custom validator to check that two fields match
	 */
	public static mustMatch(controlName: string, matchingControlName: string) {
		return (formGroup: FormGroup) => {
			const control = formGroup.controls[controlName];
			const matchingControl = formGroup.controls[matchingControlName];

			if (matchingControl.errors && !matchingControl.errors.notSamePasswords) {
				// return if another validator has already found an error on the matchingControl
				return;
			}

			// set error on matchingControl if validation fails
			if (control.value !== matchingControl.value) {
				matchingControl.setErrors({notSamePasswords: true});
			} else {
				matchingControl.setErrors(null);
			}
		};
	}

	public static email(control: FormControl) {
		if (!control.value || control.value.trim().length < 1) {
			return null;
		}
		return FormValidators.emailRegex.test(control.value) ? null : {'email': true};
	}

	public static isObject(control: AbstractControl) {
		const selection = control.value;
		if (!isNullOrEmpty(selection) && typeof selection !== 'object') {
			return {incorrect: true};
		}
		return null;
	}

	public static inputErrorOnArrayLength(inputControlName: string, arrayControlName: string) {
		return (formGroup: FormGroup) => {
			const inputControl = formGroup.controls[inputControlName];
			const arrayControl = formGroup.controls[arrayControlName];

			if (arrayControl.value.length && inputControl.value.length) {
				inputControl.setErrors({expertiseProfessionsLimit: true});
			}
		};
	}

	public static billingType(formGroup: FormGroup) {
		let isOneChecked = false;
		let lastControl;

		Object.keys(formGroup.controls).forEach(control => {
			isOneChecked = isOneChecked || formGroup.controls[control].value;
			lastControl = formGroup.controls[control];
		});

		if (!isOneChecked) {
			lastControl.setErrors({min_one_required: true});
			lastControl.markAsDirty();
		} else {
			lastControl.setErrors(null);
		}
	}

	public static validateSameDayInterval(): ValidatorFn {
		return (group: FormGroup): { [key: string]: any } => {
			if (!group) {
				return null;
			}
			const inputStartDate = group.get('startDate').value;
			const inputEndDate = group.get('endDate').value;
			const inputStartTime = group.get('startTime');
			const inputEndTime = group.get('endTime');
			const ignoreTime = group.get('allDay').value;

			const invalidStartTime = !inputStartTime.value || inputStartTime.value.trim().length < 1;
			const invalidEndTime = !inputEndTime.value || inputEndTime.value.trim().length < 1;

			if (ignoreTime) {
				inputStartTime.setErrors(null);
				inputEndTime.setErrors(null);
				return null;
			} else if (invalidStartTime || invalidEndTime) {
				inputStartTime.setErrors(invalidStartTime ? {invalidInterval: true} : null);
				inputEndTime.setErrors(inputEndTime ? {invalidInterval: true} : null);
				return {invalidInterval: true};
			}

			if (inputStartDate && inputEndDate && inputStartTime && inputEndTime &&
				DateUtils.getDateSafe(inputStartDate).getTime() === DateUtils.getDateSafe(inputEndDate).getTime()) {

				const startHours: number = +inputStartTime.value.split(':')[0];
				const startMinutes: number = +inputStartTime.value.split(':')[1];

				const endHours: number = +inputEndTime.value.split(':')[0];
				const endMinutes: number = +inputEndTime.value.split(':')[1];

				if (endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)) {
					inputStartTime.setErrors({invalidInterval: true});
					inputEndTime.setErrors({invalidInterval: true});
					return {invalidInterval: true};
				}

				inputStartTime.setErrors(null);
				inputEndTime.setErrors(null);
				return null;
			}

			inputStartTime.setErrors(null);
			inputEndTime.setErrors(null);
			return null;
		};
	}

	public static location(group, validLocation): void {
		// TODO: maybe refactor as validator/asyncValidator
		if (group.get('postalCode').touched || group.get('city').touched) {
			let extract;
			if (validLocation) {
				extract = validLocation.find(obj => {
						return +obj.postalCode === +group.get('postalCode').value && obj.city === group.get('city').value
					}
				);
			}

			const ERROR = !extract ? {matchZipCityObject: true} : null;

			if (!!group.get('postalCode').value && !!group.get('city').value) {
				group.get('postalCode').setErrors(ERROR);
				group.get('postalCode').markAsTouched();
				group.get('city').setErrors(ERROR);
				group.get('city').markAsTouched();
			}
		}
	}

	public static updateFormGroupValidity(formGroup: FormGroup): void {
		formGroup.markAllAsTouched();
		formGroup.markAsDirty();
	}

	public static isServiceNameUnique(control: AbstractControl, services: MedicalServiceDTO[]): ValidatorFn  {
		const duplicate = services.some(item => item.name?.toLowerCase() === control.value?.toLowerCase());
		return (_: AbstractControl): { [key: string]: boolean } | null => {
			return duplicate ? { ['duplicate'] : true } : null;
		};
	}
}


